import React, { useState } from 'react';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const InviteCodeCreatedModal = ({ isOpen, onClose, plaintext }) => {
    const [copied, setCopied] = useState(false);
    const { t } = useLanguage();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(plaintext);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            const el = document.createElement('textarea');
            el.value = plaintext;
            el.style.position = 'fixed';
            el.style.opacity = '0';
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleClose = () => {
        setCopied(false);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.INVITE_CODE_CREATED_TITLE)}
            subtitle="Share this code with the person you want to invite"
            size="medium"
            footer={
                <Button variant="primary" size="medium" onClick={handleClose}>
                    {t(KEYS.COMMON_DONE)}
                </Button>
            }
        >
            <div className="space-y-4">
                {/* Warning */}
                <div className="flex items-start gap-3 p-3 bg-status-overdue-bg rounded border border-status-overdue/20">
                    <AlertTriangle className="w-4 h-4 text-status-overdue flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-status-overdue font-medium">
                        {t(KEYS.INVITE_CODE_CREATED_BODY)}
                    </p>
                </div>

                {/* Code display */}
                <div>
                    <p className="label-base mb-1.5">{t(KEYS.INVITE_CODE_CREATED_LABEL)}</p>
                    <div className="flex items-stretch gap-2">
                        <div className="flex-1 bg-surface-overlay border border-surface-border rounded px-3 py-2 overflow-x-auto">
                            <code className="text-xs font-mono text-accent break-all whitespace-pre-wrap">
                                {plaintext}
                            </code>
                        </div>
                        <button
                            onClick={handleCopy}
                            title={copied ? t(KEYS.COMMON_COPIED) : t(KEYS.COMMON_COPY)}
                            className={`
                                flex-shrink-0 flex items-center gap-1.5 px-3 rounded border text-xs font-medium transition-colors
                                ${copied
                                    ? 'bg-status-paid-bg border-status-paid/30 text-status-paid'
                                    : 'bg-surface-overlay border-surface-border text-text-muted hover:text-accent hover:border-accent/40'
                                }
                            `}
                        >
                            {copied ? (
                                <>
                                    <Check className="w-3.5 h-3.5" />
                                    {t(KEYS.COMMON_COPIED)}
                                </>
                            ) : (
                                <>
                                    <Copy className="w-3.5 h-3.5" />
                                    {t(KEYS.COMMON_COPY)}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <p className="text-xs text-text-muted">
                    The recipient must enter this code when registering a new account.
                </p>
            </div>
        </Modal>
    );
};

export default InviteCodeCreatedModal;
